import {
  Outlet,
  Navigate,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminModeToggle from "@/components/AdminModeToggle";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Settings,
  Users,
  CreditCard,
  Globe,
  HeadphonesIcon,
  LogOut,
  ArrowUpCircle,
  Wallet,
  Loader2,
} from "lucide-react";

import logo from "@/assets/logo.svg";

export default function AdminLayout() {
  const { isAdmin, adminMode, user, isAdminCheckComplete } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (!isAdminCheckComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin || !adminMode) {
    return <Navigate to="/home" replace />;
  }

  const adminTabs = [
    {
      value: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      path: "/admin",
    },
    { value: "users", label: "Users", icon: Users, path: "/admin/users" },
    { value: "cards", label: "Cards", icon: CreditCard, path: "/admin/cards" },
    {
      value: "prefund",
      label: "Prefund",
      icon: Wallet,
      path: "/admin/prefund",
    },
    {
      value: "topups",
      label: "Top-ups",
      icon: ArrowUpCircle,
      path: "/admin/topups",
    },
    // { value: "kyc", label: "KYC", icon: Shield, path: "/admin/kyc" },
    {
      value: "support",
      label: "Support",
      icon: HeadphonesIcon,
      path: "/admin/support",
    },
    {
      value: "site",
      label: "Site Settings",
      icon: Globe,
      path: "/admin/site-settings",
    },
    {
      value: "admin",
      label: "Admin Settings",
      icon: Settings,
      path: "/admin/settings",
    },
  ];

  const currentTab =
    adminTabs.find((tab) => location.pathname === tab.path)?.value ||
    "dashboard";

  return (
    <div className="min-h-screen bg-background">
      {/* Fixed Header Container */}
      <div className="fixed top-0 left-0 right-0 z-50">
        {/* Admin Header */}
        <div className="border-b bg-card">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <h1 className="text-base sm:text-xl font-semibold flex items-center gap-2">
                  <img
                    src={logo}
                    alt="FellasCard"
                    className="h-10 sm:h-12 w-auto"
                  />
                  <span className="ml-2 sm:ml-4 text-sm sm:text-base">
                    FellasCard Admin
                  </span>
                </h1>
                {/* <Badge variant="destructive">Admin Mode</Badge> */}
              </div>
              <div className="flex items-center gap-4">
                <AdminModeToggle variant="outline" />
                <span className="text-sm text-muted-foreground">
                  {user?.email}
                </span>
                {/* logout button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/logout")}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Also Fixed */}
        <div className="border-b bg-card/95 backdrop-blur">
          <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
            <Tabs value={currentTab} className="w-full">
              <TabsList className="flex flex-wrap sm:grid sm:grid-cols-8 w-full bg-transparent h-auto p-0">
                {adminTabs.map((tab) => {
                  const Icon = tab.icon;

                  return (
                    <Link
                      key={tab.value}
                      to={tab.path}
                      className="w-1/4 sm:w-auto"
                    >
                      <TabsTrigger
                        value={tab.value}
                        className={cn(
                          // Base styles
                          "flex flex-col sm:flex-row gap-0.5 sm:gap-2",
                          "py-2.5 px-1 sm:px-4 rounded-none",
                          "border-b-2 border-transparent",
                          "transition-all duration-200",
                          "w-full",
                          // Active state
                          "data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-blue-950/30",
                          "data-[state=active]:border-blue-500",
                          "data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400",
                          // Hover state
                          "hover:bg-blue-50/50 dark:hover:bg-blue-950/20",
                          "hover:text-blue-600 dark:hover:text-blue-400",
                          "hover:border-blue-300 dark:hover:border-blue-700"
                        )}
                      >
                        <Icon className="h-4 w-4 sm:h-4 sm:w-4" />
                        <span className="text-[10px] sm:text-sm text-center leading-tight">
                          {tab.label}
                        </span>
                      </TabsTrigger>
                    </Link>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Main Content - Add padding-top to account for fixed header */}
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0"
        style={{ paddingTop: "calc(64px)" }}
      >
        <Outlet />
      </div>
    </div>
  );
}
